import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'urql'
import { RECENT_SYNC_JOBS_QUERY, SYNC_JOB_PROGRESS_FRAGMENT } from 'app/components/integrations-locations/graphql'
import { getFragmentData } from 'app/generated/gql'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import type { SyncProgressByLocation } from 'app/types/integrations'
import { useUser } from 'app/utils'
import { groupRunningSyncJobs } from 'lib/syncProgress'

const POLL_MS = 10_000
// Kolla starts a run per location every hour and runs overlap, so 400 Kolla jobs reach back about
// two hours: far enough for every location's newest run. The other syncs are much rarer.
const RECENT_JOBS_VARIABLES = { pagination: { limit: 400 } }

/**
 * Live PMS sync progress, polled from the ops schema. Background job progress is super-user
 * only on the API, so everyone else gets an empty map and no requests.
 */
export function useSyncProgress(onSyncFinished: () => void): SyncProgressByLocation {
  const user = useUser()
  const isSuper = user?.type === `super`
  // The poll is a background enhancement; a failed tick should not toast every 10 seconds.
  const context = useMemo(() => ({ url: OPS_URL, suppressErrorToast: true }), [])
  const [{ data }, reexecuteQuery] = useQuery({
    query: RECENT_SYNC_JOBS_QUERY,
    variables: RECENT_JOBS_VARIABLES,
    context,
    pause: !isSuper,
    requestPolicy: `network-only`,
  })

  useEffect(() => {
    if (!isSuper) return
    const id = setInterval(() => reexecuteQuery({ requestPolicy: `network-only` }), POLL_MS)
    return () => clearInterval(id)
  }, [isSuper, reexecuteQuery])

  const progress = useMemo(() => {
    if (!data) return {}
    const pages = [data.kolla, data.sikka, data.carestack, data.ascend]
    return groupRunningSyncJobs(pages.flatMap((page) => getFragmentData(SYNC_JOB_PROGRESS_FRAGMENT, page.nodes)))
  }, [data])

  // A job leaving the running list means its sync just finished, so the row's status and
  // last update are stale until the page refetches them.
  const runningJobIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    const nextIds = new Set(
      Object.values(progress).flatMap((byIntegration) => Object.values(byIntegration).map((job) => job!.jobId)),
    )
    const finished = [...runningJobIds.current].some((jobId) => !nextIds.has(jobId))
    runningJobIds.current = nextIds
    if (finished) onSyncFinished()
  }, [progress])

  return progress
}
